import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import { useTranslation } from "common/locales";
import { DiaryEntry, useDeleteDiaryEntry } from "model";
import { useState } from "react";

interface Props {
  diaryEntry: DiaryEntry;
  setOpenDiaryEntryDialogGuid: (value: string | null) => void;
  canEdit: boolean;
  farmId: string;
}

export default function DiaryEntryConfirmDialog({
  diaryEntry,
  setOpenDiaryEntryDialogGuid,
  canEdit,
  farmId,
}: Props) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { notify } = useNotification();
  const deleteDiaryEntryMutation = useDeleteDiaryEntry();

  return (
    <>
      <Tooltip title={t("diaryEntry.delete.tooltip")}>
        {canEdit ? (
          <Button color="secondary" onClick={() => setOpen(true)}>
            {t("diaryEntry.delete.label")}
          </Button>
        ) : (
          <Button
            color="primary"
            onClick={() => setOpenDiaryEntryDialogGuid(null)}
          >
            {t("diaryEntry.view.close.label")}
          </Button>
        )}
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle maxWidth="300px">
          {t("diaryEntry.areYouSure.label")}
        </DialogTitle>
        <DialogActions>
          <Button color="primary" onClick={() => handleDelete()}>
            {t("diaryEntry.delete.confirm.label")}
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              mixpanel.track("diaryEntry clicked Cancel");
              setOpen(false);
            }}
          >
            {t("diaryEntry.cancel.label")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  async function handleDelete() {
    mixpanel.track("diaryEntry clicked Delete");
    try {
      await deleteDiaryEntryMutation.mutateAsync({
        diaryEntry,
        farmId,
      });
      notify(t("diaryEntry.deleted.notification"));
      mixpanel.track("diaryEntry deleted");
      setOpen(false);
      setOpenDiaryEntryDialogGuid(null);
    } catch (error) {
      console.error("Failed to delete diary entry:", error);
      notify(t("diaryEntry.delete.error") || "Failed to delete diary entry");
    }
  }
}
